import {
  MULTIPLE_VALUES_INDICATOR,
  processFormValues,
  type SkuQualificationGroupModel,
  type SkuQualificationRow,
} from "helpers/skuQualificationHelpers";
import {
  ISkuQualificationPreviewResponse,
  ISkuQualificationRequest,
  IValidateCourseNumberResponse,
  SkuQualificationSubjectAreaRequest,
} from "services/KnowfullyAdminClient";
import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import { toUtcMidnight } from "utils/dateUtils";
import type { CreditLine } from "./CreditsSlot";
import { NO_LINK_VALUE, type ReferenceRow } from "./qualificationLinkState";

export const withAcronym = (name?: string, acronym?: string) =>
  acronym ? `${name} (${acronym})` : (name ?? "-");

// The wizard writes one SkuQualification per (entry x profession), so its form holds every
// request field except the two the fan-out supplies.
export type WizardFormValues = Omit<
  ISkuQualificationRequest,
  "qualificationId" | "professionId" | "id" | "duplicateHandling" | "subjectAreas"
> & {
  professionIds: string[];
  // The editable rows behind subjectAreas. They carry a React key the request has no room
  // for, so they are mapped on save. Left undefined the save does not touch the stored
  // splits; an empty array clears them.
  creditLines?: CreditLine[];
  // With entries under several accreditors, each accreditor's own rows, keyed by its id:
  // an accreditor only takes the Subject Areas it recognizes.
  creditLinesByAccreditor?: Record<string, CreditLine[]>;
};

export const emptyWizardFormValues = (): WizardFormValues => ({
  professionIds: [],
  stateIds: [],
  courseNumber: "",
  approvalCode: "",
  notes: "",
});

// An Accreditation Body is a Qualification, so the entry's accreditationBodyId is the
// QualificationId. Profession and state sentinels stay in processFormValues so the wizard
// resolves them exactly like the form it replaces.
// How a save identifies what it replaces. A group's own id is synthetic, so its members travel
// as childSkuQualificationIds and the save merges field by field. A single row travels as its
// own id, the only path where clearing a field actually clears it.
export type EditTarget = {
  skuQualificationId?: string;
  childSkuQualificationIds?: string[];
};

// Every fanned member of a group keeps the id of the SkuQualification it came from, so a group
// drawn from one SkuQualification shares its stored credit lines and can show them.
export const creditLinesSourceId = (target?: EditTarget): string | undefined => {
  if (target?.skuQualificationId) return target.skuQualificationId;
  const ids = new Set(target?.childSkuQualificationIds);
  return ids.size === 1 ? Array.from(ids)[0] : undefined;
};

// A row the author never gave a Subject Area to is not a split, so it is dropped rather than
// sent with an empty id. Undefined in, undefined out: the save leaves stored splits alone.
export const toSubjectAreaRequests = (
  lines?: CreditLine[],
): SkuQualificationSubjectAreaRequest[] | undefined =>
  lines
    ?.filter((line) => !!line.subjectAreaId)
    .map((line) =>
      SkuQualificationSubjectAreaRequest.fromJS({
        id: line.id,
        subjectAreaId: line.subjectAreaId,
        credits: line.credits,
      }),
    );

export const toSkuQualificationRequests = (
  entries: ReferenceRow[],
  values: WizardFormValues,
  editTarget?: EditTarget,
): ISkuQualificationRequest[] => {
  const mixedAccreditors = new Set(entries.map((entry) => entry.accreditationBodyId)).size > 1;
  const linesFor = (accreditorId: string) =>
    mixedAccreditors ? values.creditLinesByAccreditor?.[accreditorId] : values.creditLines;

  const requests = entries.flatMap((entry, entryIndex) =>
    values.professionIds.map((selectedProfessionId, professionIndex) => {
      const { professionId, stateIds, useQualificationApprovals } =
        processFormValues(selectedProfessionId, values.stateIds);

      // Only the first record can claim the edited row; the rest are professions being added.
      // A record that expands server side into several rows claims nothing, since one id cannot
      // identify many rows: that is every approved profession, or more than one state.
      const claimsEditedRow =
        entryIndex === 0 &&
        professionIndex === 0 &&
        !useQualificationApprovals &&
        (stateIds?.length ?? 0) <= 1;

      return {
        id: claimsEditedRow ? editTarget?.skuQualificationId : undefined,
        qualificationId: entry.accreditationBodyId,
        professionId,
        stateIds,
        useQualificationApprovals,
        courseNumber: values.courseNumber,
        approvalCode: values.approvalCode,
        approvalDate: toUtcMidnight(values.approvalDate) ?? undefined,
        releaseDate: toUtcMidnight(values.releaseDate) ?? undefined,
        creditSubmissionExpirationDate:
          toUtcMidnight(values.creditSubmissionExpirationDate) ?? undefined,
        credits: values.credits,
        minimumCredit: values.minimumCredit,
        partialCreditIncrement: values.partialCreditIncrement,
        notes: values.notes,
        childSkuQualificationIds: editTarget?.childSkuQualificationIds,
        subjectAreas: toSubjectAreaRequests(linesFor(entry.accreditationBodyId)),
      };
    }),
  );

  // The licensure body is not persisted, so entries sharing an Accreditation Body resolve to
  // the same record: one request each, not one per entry.
  return distinctByQualificationAndProfession(requests);
};

// Each gate below runs once per request and the dialogs take a single response, so the
// responses are folded into that same shape instead of changing the dialogs.
export const mergeCourseNumberValidations = (
  responses: IValidateCourseNumberResponse[],
): IValidateCourseNumberResponse => ({
  hasConflict: responses.some((response) => response.hasConflict),
  existingSkuCourseNumbers: responses.flatMap(
    (response) => response.existingSkuCourseNumbers ?? [],
  ),
});

export const mergePreviews = (
  responses: ISkuQualificationPreviewResponse[],
): ISkuQualificationPreviewResponse => ({
  existingQualifications: responses.flatMap(
    (response) => response.existingQualifications ?? [],
  ),
  totalNew: responses.reduce(
    (total, response) => total + (response.totalNew ?? 0),
    0,
  ),
  totalExisting: responses.reduce(
    (total, response) => total + (response.totalExisting ?? 0),
    0,
  ),
});

// A grouped row aggregates its members, so a field they disagree on reads as the multiple
// values marker. Editing must leave those blank rather than write the marker back.
const cleanValue = <T,>(
  value: T | typeof MULTIPLE_VALUES_INDICATOR,
): T | undefined =>
  value === MULTIPLE_VALUES_INDICATOR ? undefined : (value as T);

const toEntry = (row: SkuQualificationRow): ReferenceRow => ({
  lb: withAcronym(row.licensureBodyName, row.licensureBodyAcronym),
  ab: withAcronym(row.accreditationBodyName, row.accreditationBodyAcronym),
  sa: row.submissionAuthorityName ?? NO_LINK_VALUE,
  licensureBodyId: row.licensureBodyId ?? "",
  accreditationBodyId: row.accreditationBodyId ?? row.qualification?.id ?? "",
});

export type WizardEditState = {
  entries: ReferenceRow[];
  initialValues: WizardFormValues;
  editTarget: EditTarget;
};

// An Accreditation Body is a Qualification, so a row with no resolved chain still knows its
// AB through the qualification it carries.
export const toWizardEditState = (
  row: SkuQualificationGroupModel,
): WizardEditState => {
  const isGroup = row.isGroup === true;
  const children = isGroup ? row.childQualifications : [];

  return {
    // A group row carries aggregated values, so reading the chain off it yields the
    // MULTIPLE_VALUES indicator instead of a body name. The members hold the real chain.
    entries: children.length > 0 ? children.map(toEntry) : [toEntry(row)],
    editTarget: isGroup
      ? {
          childSkuQualificationIds: children
            .map((child) => child.id)
            .filter((id): id is string => !!id),
        }
      : { skuQualificationId: row.id },
    initialValues: {
      // A row saved with the Any profession stores none; the sentinel resolves back on save.
      professionIds: [row.profession?.id || ANY_PROFESSION_ID],
      stateIds: isGroup
        ? children
            .map((child) => child.stateId)
            .filter((id): id is string => !!id)
        : row.stateId
          ? [row.stateId]
          : [],
      courseNumber: cleanValue(row.courseNumber),
      approvalCode: cleanValue(row.approvalCode),
      approvalDate: cleanValue(row.approvalDate),
      releaseDate: cleanValue(row.releaseDate),
      creditSubmissionExpirationDate: cleanValue(
        row.creditSubmissionExpirationDate,
      ),
      credits: cleanValue(row.credits),
      minimumCredit: cleanValue(row.minimumCredit),
      partialCreditIncrement: cleanValue(row.partialCreditIncrement),
      notes: cleanValue(row.notes),
    },
  };
};

// Requests sharing a (qualification, profession) resolve to the same record server side, so
// keeping the first avoids issuing an identical call for every one of them.
export const distinctByQualificationAndProfession = (
  requests: ISkuQualificationRequest[],
): ISkuQualificationRequest[] => {
  const seen = new Set<string>();

  return requests.filter((request) => {
    const key = `${request.qualificationId}|${request.professionId ?? ""}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};
